import {
  FieldText,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { NotificationPayloadT } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import * as React from 'react';
import {
  Field,
  Form,
  InjectedFormProps,
  reduxForm,
  SubmissionError,
} from 'redux-form';

import { ajaxSend } from 'api/api';
import { RouteComponentProps } from 'hocs/router';

import { stringToHexColor } from '../../../../utils/color/color';
import ColorField from '../../../components/shared/util/ColorField';
import { WithTagsInjectedProps } from '../../../hocs/tags/withTags';

import styles from './tagsCreateModal.module.scss';
import { validate } from './utils';

export const FORM_NAME = 'addTagsForm';

export type TagsCreateModalOwnProps = {
  isOpen: boolean;
  onClose: (canceled?: boolean) => void;
  newTagName?: string;
  selectTag?: (tag) => void;
};

enum Fields {
  name = 'name',
  color = 'color',
}

export interface TagsCreateModalFormValues {
  [Fields.name]: string;
  [Fields.color]: string;
}

export type TagsCreateModalDispatchProps = {
  notificationAdd: (payload: NotificationPayloadT) => void;
  changeFieldValue: (field: string, value: any) => void;
};

export type TagsCreateModalStateProps = {};

type Props = RouteComponentProps &
  WithTagsInjectedProps &
  TagsCreateModalOwnProps &
  TagsCreateModalDispatchProps &
  TagsCreateModalStateProps;

type InjectedProps = InjectedFormProps<TagsCreateModalFormValues, Props>;

class TagsCreateModalView extends React.PureComponent<Props & InjectedProps> {
  componentDidUpdate(prevProps: Props) {
    const {
      isOpen,
      reset,
      newTagName,
      params: { pathname },
      initialValues,
    } = this.props;
    if (!isOpen && prevProps.isOpen) {
      reset();
    } else if (isOpen && !prevProps.isOpen) {
      ampli.tagsCreateModalOpened({
        path: pathname ?? '',
        tagName: initialValues?.name ?? newTagName ?? '',
      });
    }

    if (prevProps.newTagName !== newTagName) {
      const color = newTagName && this.getHexValue(newTagName);
      this.props.changeFieldValue('name', newTagName);
      this.props.changeFieldValue('color', color);
    }
  }

  componentWillUnmount() {
    const { reset } = this.props;

    reset();
  }

  @boundMethod
  async doesTagAlreadyExist(name: string): Promise<boolean> {
    const response: any = await ajaxSend({
      path: '/v2/search',
      params: {
        query: name,
        strict: true,
        scope: 'tag',
      },
    });

    const { hits } = response.data[0];

    if (hits.length) {
      const hit = hits.find(
        (hit) => hit.name.toLowerCase() === name.toLowerCase(),
      );

      if (hit) {
        return Promise.resolve(true);
      }
    }

    return Promise.resolve(false);
  }

  @boundMethod
  async handleFormSubmit(values: TagsCreateModalFormValues) {
    const {
      tags,
      reset,
      onClose,
      notificationAdd,
      selectTag,
      params: { path },
    } = this.props;
    const { create } = tags;

    ampli.tagsCreateModalFormSubmitted({
      tagName: values[Fields.name],
      path,
    });

    const doesTagAlreadyExist = await this.doesTagAlreadyExist(values.name);

    if (doesTagAlreadyExist) {
      const notificationOptions = {
        title: 'Can not create tag',
        text: `A tag with the same name already exists`,
        icon: 'i-exclamation',
        className: 'danger',
      };

      notificationAdd(notificationOptions);

      return;
    }

    const payload = {
      name: values.name,
      color: values.color || stringToHexColor(values.name),
    };
    create(payload)
      .then((r) => {
        const notificationOptions = {
          title: 'Tag created',
          text: `"${values.name}" tag has been created`,
          icon: 'i-done',
          className: 'success',
          time: 5000,
        };

        notificationAdd(notificationOptions);
        selectTag && selectTag(payload);
        reset();
        onClose();
      })
      .catch((e) => {
        const notificationOptions = {
          title: 'An error occurred',
          text: `An error occurred when trying to create the "${values.name}" tag`,
          icon: 'i-exclamation',
          className: 'danger',
        };

        notificationAdd(notificationOptions);
        throw new SubmissionError({ name: 'Tag already exist' });
      });
  }

  @boundMethod
  handleColorChange(color) {
    ampli.tagsCreateModalColorChanged({
      color,
      path: this.props.params.pathname ?? '',
    });
  }

  @boundMethod
  getHexValue(tag: string) {
    let color = '';
    if (
      tag !== undefined &&
      tag !== null &&
      tag !== '' &&
      typeof tag == 'string'
    ) {
      color = stringToHexColor(tag);
    }
    return color;
  }

  @boundMethod
  nameChanged(e: React.ChangeEvent<HTMLInputElement>) {
    const tag = e.target.value.trim();
    const color = this.getHexValue(tag);

    this.props.changeFieldValue('color', color);
  }

  render() {
    const { isOpen, onClose, handleSubmit } = this.props;

    return (
      <Modal
        isOpen={isOpen}
        onClose={() => onClose(true)}
        className={styles.root}
      >
        <Form onSubmit={handleSubmit(this.handleFormSubmit)}>
          <ModalHead title="Add a new tag" onClose={() => onClose(true)} />
          <ModalBody className={styles.tagModalBody}>
            <Field
              label="Name"
              name="name"
              maxLength={255}
              component={FieldText}
              type="text"
              placeholder="Name..."
              required
              onChange={this.nameChanged}
            />
            <Field
              label="Tag color"
              name="color"
              className={styles.colorField}
              component={ColorField}
              showSpinner={false}
              required
              onChange={this.handleColorChange}
            />
          </ModalBody>
          <ModalFoot
            cancelOnClick={() => onClose(true)}
            cancelName="Cancel"
            confirmOnClick={handleSubmit(this.handleFormSubmit)}
            confirmName="Save"
            confirmProps={{
              disabled: this.props.invalid || this.props.pristine,
            }}
          />
        </Form>
      </Modal>
    );
  }
}

export const TagsCreateModalForm = reduxForm<TagsCreateModalFormValues, Props>({
  form: FORM_NAME,
  enableReinitialize: true,
  validate,
})(TagsCreateModalView);
